<template>
  <div>
    <!-- advance search input -->
    <b-row class="bg-white px-1 py-2 mx-0 filter-container">
      <b-col
        cols="12"
        md="4"
      >
        <b-form-group
          class="form-label m-0"
        >
          <label>
            Ad account
          </label>
          <v-select
            v-model="adAccountSelected"
            label="name"
            class="ads-account-select"
            :options="adAccountOptions"
            :clearable="false"
            :searchable="false"
            @input="handelSelectAdAccount"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes">
                <feather-icon
                  icon="ChevronDownIcon"
                  size="13"
                />
              </span>
            </template>

            <template #option="{ name, value }">
              <div class="d-flex justify-content-between mr-2">
                <span class="mb-0">
                  {{ name }}
                </span>
                <span class="mb-0">
                  {{ value }}
                </span>
              </div>
            </template>
          </v-select>
        </b-form-group>
        <p
          v-if="adAccountSelected && adAccountSelected.timezone"
          class="mt-1 mb-0 d-flex align-items-center"
        >
          ({{ etcToUTC(adAccountSelected.timezone) }}) {{ adAccountSelected.displayTimezone }}
          <feather-icon
            v-b-tooltip.hover.top.v-light="'This time zone is based on your ad account settings.'"
            class="ml-25"
            icon="HelpCircleIcon"
            size="13"
          />
        </p>
      </b-col>
      <b-col
        cols="12"
        md="5"
      >
        <b-form-group
          class="form-label m-0"
        >
          <label>
            Search by Campaign ID
          </label>
          <b-form-input
            v-model="campaignIds"
            name="campaignIds"
            class="input-height"
            style="height: 38px"
            placeholder="Ex: 1843947038115970, 1843945136585842,...."
            @input="handleSearchCampaignId"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-tabs class="bg-white mt-2 mt-md-4 tab-campaign">
      <b-tab>
        <template #title>
          <div class="d-flex align-items-center">
            <p>
              {{ $t('campaign.textCampaign') }}
            </p>
            <p
              v-if="campaignIdsSelected.length > 0"
              class="selected-number d-flex justify-content-center align-items-center"
            >
              <span>{{ campaignIdsSelected.length }}</span>
            </p>
          </div>
        </template>
        <div>
          <campaign-table
            :advertiser-id="adAccountSelected"
            :campaign-ids="campaignIds"
            @handleSelectCampaigns="handleSelectCampaigns"
            @fetchListCampaign="fetchListCampaign"
          />
        </div>
      </b-tab>
      <b-tab>
        <template #title>
          <div class="d-flex align-items-center">
            <p>
              {{ $t('campaign.textAdGroup') }}
            </p>
            <p
              v-if="adGroupIdsSelected.length > 0"
              class="selected-number d-flex justify-content-center align-items-center"
            >
              <span>{{ adGroupIdsSelected.length }}</span>
            </p>
          </div>
        </template>
        <div>
          <ad-group-table
            :advertiser-id="adAccountSelected"
            :campaign-ids="campaignIds"
            :campaign-ids-selected="campaignIdsSelected"
            @handleSelectAdGroups="handleSelectAdGroups"
            @fetchListAdGroup="fetchListAdGroup"
          />
        </div>
      </b-tab>
      <b-tab>
        <template #title>
          <div class="d-flex align-items-center">
            <p>
              {{ $t('campaign.textAd') }}
            </p>
            <p
              v-if="adIdsSelected.length > 0"
              class="selected-number d-flex justify-content-center align-items-center"
            >
              <span>{{ adIdsSelected.length }}</span>
            </p>
          </div>
        </template>
        <div>
          <ad-table
            :advertiser-id="adAccountSelected"
            :campaign-ids="campaignIds"
            :campaign-ids-selected="campaignIdsSelected"
            :ad-group-ids-selected="adGroupIdsSelected"
            @handleSelectAds="handleSelectAds"
            @fetchListAd="fetchListAd"
          />
        </div>
      </b-tab>
    </b-tabs>
  </div>
</template>
<script lang="js">
import {
  BFormGroup, BRow, BCol, BTabs, BTab, VBTooltip, BFormInput,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { createNamespacedHelpers } from 'vuex'
import get from 'lodash/get'
import CampaignTable from '@/views/campaign/components/CampaignTable.vue'
import AdGroupTable from '@/views/campaign/components/AdGroupTable.vue'
import AdTable from '@/views/campaign/components/AdTable.vue'
import Ripple from 'vue-ripple-directive'
import campaignMixin from '@/mixins/campaignMixin'

const { mapActions, mapGetters } = createNamespacedHelpers('adsAccount')
const { mapActions: mapActionsCampaign } = createNamespacedHelpers('campaign')

export default {
  components: {
    BFormInput,
    CampaignTable,
    AdGroupTable,
    AdTable,
    BFormGroup,
    vSelect,
    BRow,
    BCol,
    BTabs,
    BTab,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  mixins: [campaignMixin],

  data() {
    return {
      adAccountSelected: null,
      adAccountOptions: [],

      campaignIdsSelected: [],
      adGroupIdsSelected: [],
      adIdsSelected: [],

      campaignIds: '',
      pageLength: 10,

      campaignFilter: {
        currentPage: 1,
        pageLength: 10,
      },

      adGroupFilter: {
        currentPage: 1,
        pageLength: 10,
      },

      adFilter: {
        currentPage: 1,
        pageLength: 10,
      },
    }
  },

  computed: {
    ...mapGetters(['listAccount', 'message', 'status', 'loading']),
  },

  async created() {
    const params = {
      page: 1,
      limit: 100,
      status: 'SHOW_ACCOUNT_STATUS_APPROVED',
    }
    await this.getAccountList(params)
    if (this.status) {
      this.adAccountOptions = get(this.listAccount, ['content'], []).filter(item => item.isWhitelistUSPC).map(item => ({
        name: item.name,
        // eslint-disable-next-line no-underscore-dangle
        value: item._id,
        timezone: item.timezone,
        displayTimezone: item.displayTimezone,
      }))

      if (localStorage.getItem('currentAdAccount')) {
        this.adAccountSelected = this.adAccountOptions?.find(item => item.value === localStorage.getItem('currentAdAccount')) || this.adAccountOptions?.[0]
      } else {
        this.adAccountSelected = this.adAccountOptions?.[0]
      }

      await this.fetchListCampaign()
      await this.fetchListAdGroup()
      await this.fetchListAd()
    }
  },

  methods: {
    ...mapActions(['getAccountList']),
    ...mapActionsCampaign(['getListCampaign', 'getListAdGroupSpp', 'getListAd']),

    async handleSearchCampaignId() {
      this.debounce = setTimeout(async () => {
        await this.fetchListCampaign()
        await this.fetchListAdGroup()
        await this.fetchListAd()
      }, 600)
    },

    handelSelectAdAccount() {
      localStorage.setItem('currentAdAccount', this.adAccountSelected.value)
      this.campaignIds = ''
      this.fetchListCampaign()
      this.fetchListAdGroup()
      this.fetchListAd()
    },

    handleSelectCampaigns(campaignIds) {
      this.campaignIdsSelected = campaignIds
    },

    handleSelectAdGroups(adGroupIds) {
      this.adGroupIdsSelected = adGroupIds
    },

    handleSelectAds(adIds) {
      this.adIdsSelected = adIds
    },

    async fetchListCampaign(page = 1, pageLength = 10) {
      if (!this.adAccountSelected || !this.adAccountSelected.value) {
        return
      }
      const params = {
        advertiserId: this.adAccountSelected.value,
        ...(this.campaignIds && { campaignIds: this.campaignIds }),
        page,
        size: pageLength,
        sortTerm: `${this.sortTerm?.type === 'desc' ? '-' : ''}create_time`,
      }
      await this.getListCampaign(params)
    },

    async fetchListAdGroup(page = 1, pageLength = 10) {
      if (!this.adAccountSelected || !this.adAccountSelected.value) {
        return
      }

      const params = {
        advertiserId: this.adAccountSelected.value,
        page,
        size: pageLength,
        sortTerm: `${this.sortTerm?.type === 'desc' ? '-' : ''}create_time`,
        ...(this.campaignIds && this.campaignIdsSelected.length === 0 && { campaignIds: this.campaignIds }),
        ...(this.campaignIdsSelected.length > 0 && { campaignIds: this.campaignIdsSelected.join(',') }),
      }
      await this.getListAdGroupSpp(params)
    },

    async fetchListAd(page = 1, pageLength = 10) {
      if (!this.adAccountSelected || !this.adAccountSelected.value) {
        return
      }

      const params = {
        advertiserId: this.adAccountSelected.value,
        page,
        size: pageLength,
        sortTerm: `${this.sortTerm?.type === 'desc' ? '-' : ''}create_time`,
        ...(this.campaignIds && this.campaignIdsSelected.length === 0 && { campaignIds: this.campaignIds }),
        ...(this.campaignIdsSelected.length > 0 && { campaignIds: this.campaignIdsSelected.join(',') }),
        ...(this.adGroupIdsSelected.length > 0 && { adgroupIds: this.adGroupIdsSelected.join(',') }),
      }
      await this.getListAd(params)
    },
  },
}
</script>

<style lang="scss" scoped>
.tab-campaign, .filter-container {
  border-radius: var(--border-radius-base);
}
.selected-number {
  background-color: #0000001a;
  color: #00000080;
  font-size: 12px;
  border-radius: 50%;
  width: 21px;
  height: 21px;
  margin-left: 8px;
}
</style>

<style lang="scss">
.tab-campaign {
  .nav-tabs {
    margin-bottom: 24px;
    padding: 16px 24px 0;

    p {
      margin-bottom: 0;
    }

    .nav-link.active {
        .selected-number {
          background-color: #e11c4e1a;
          color: #e11c4e;
        }
    }
  }
}
</style>
